import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Printer, X } from 'lucide-react';
import { clsx } from 'clsx';

import { CARGOS, data as formatarData, numero, percentual, reais } from '../lib/formato.ts';
import type { CabecalhoFechamento, LinhaResultado } from '../lib/tipos.ts';
import { Botao } from './ui.tsx';

export type ModoComprovante = 'detalhado' | 'resumido';

/**
 * Comprovante individual — o documento que se entrega ao funcionário.
 *
 * Dois modos, porque servem a conversas diferentes: o resumido é o "quanto eu
 * recebi", que acompanha o pagamento; o detalhado é o "por que foi esse valor",
 * que responde uma contestação e por isso mostra as contagens e o memorial.
 *
 * Impressão pelo navegador em vez de biblioteca de PDF no servidor: o "Salvar
 * como PDF" resolve, e o motor de impressão do navegador entrega tipografia
 * melhor do que valeria a pena reimplementar.
 */
export function FolhaComprovante({
  fechamento,
  linha,
  modo,
  primeira = true,
}: {
  fechamento: CabecalhoFechamento;
  linha: LinhaResultado;
  modo: ModoComprovante;
  primeira?: boolean;
}) {
  const detalhado = modo === 'detalhado';
  const temBalizador = linha.balizRotulo !== 'NÃO APLICÁVEL';

  return (
    <article
      className={clsx(
        'bg-white font-sans',
        !primeira &&
          'quebra-antes mt-10 border-t-4 border-double border-slate-300 pt-10 print:mt-0 print:border-0 print:pt-0',
      )}
    >
      <header className="evitar-quebra border-b-4 border-slate-900 pb-4">
        <div className="flex items-baseline justify-between gap-6">
          <div>
            <p className="font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase">
              Skinlaser · Central de Agendamentos
            </p>
            <h1 className="mt-2 font-serif text-2xl leading-tight font-medium text-slate-900">
              Comprovante de Premiação
            </h1>
          </div>
          <div className="text-right">
            <p className="font-mono text-[10px] tracking-[0.15em] text-slate-500 uppercase">
              Competência
            </p>
            <p className="num mt-1 text-lg font-semibold text-slate-900">
              {fechamento.competencia}
            </p>
          </div>
        </div>
      </header>

      <section className="evitar-quebra grid gap-x-10 gap-y-3 border-b border-slate-200 py-5 sm:grid-cols-3">
        <Dado rotulo="Funcionário" valor={linha.nome} />
        <Dado rotulo="Cargo" valor={CARGOS[linha.cargoCodigo] ?? linha.cargoCodigo} />
        <Dado
          rotulo="Período apurado"
          valor={`${formatarData(fechamento.periodoInicio)} a ${formatarData(fechamento.periodoFim)}`}
        />
        {linha.funcionarioFeegowId != null && detalhado && (
          <Dado rotulo="Matrícula" valor={String(linha.funcionarioFeegowId)} />
        )}
      </section>

      <section className="evitar-quebra py-5">
        <Titulo>Apuração</Titulo>
        <dl className="mt-3 space-y-1.5 text-sm">
          <Item
            rotulo="Taxa de ocupação da agenda"
            detalhe={
              detalhado
                ? `${percentual(linha.ocupacaoTaxa)} — ${linha.ocupacaoFase}`
                : linha.ocupacaoFase
            }
            valor={reais(linha.ocupacaoValor)}
          />
          <Item
            rotulo="Taxa de comparecimento"
            detalhe={
              detalhado
                ? `${numero(linha.compAtendido)} de ${numero(linha.compAgendado)} consultas = ${percentual(linha.compTaxa)} — ${linha.compFase}`
                : `${percentual(linha.compTaxa)} — ${linha.compFase}`
            }
            valor={reais(linha.compValor)}
          />
          <Item rotulo="Soma das premiações" valor={reais(linha.somaPremiacao)} forte />

          {temBalizador && (
            <Item
              rotulo="Balizador de produção"
              detalhe={
                detalhado
                  ? `${numero(linha.balizVolume)} agendamentos, ${percentual(linha.balizPercentual)} da média de ${numero(linha.balizMedia)} — ${linha.balizRotulo}`
                  : linha.balizRotulo
              }
              valor={`× ${percentual(linha.balizFator)}`}
            />
          )}
        </dl>
      </section>

      <section className="evitar-quebra flex items-baseline justify-between border-y-2 border-slate-900 py-4">
        <span className="font-medium text-slate-900">Total da premiação</span>
        <span className="num text-2xl font-semibold text-slate-900">{reais(linha.totalFinal)}</span>
      </section>

      {detalhado && linha.memorial.length > 0 && (
        <section className="evitar-quebra py-5">
          <Titulo>Memorial de cálculo</Titulo>
          <ol className="mt-3 space-y-1.5 text-[13px] text-slate-700">
            {linha.memorial.map((m, i) => (
              <li key={i} className="flex gap-2">
                <span className="font-mono text-[11px] text-slate-400">{i + 1}.</span>
                <span>{m}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      <footer className="evitar-quebra border-t border-slate-200 pt-4 text-[11px] leading-relaxed text-slate-500">
        <p>
          Regras aplicadas: {fechamento.regrasNome ?? '—'} · fechamento versão {fechamento.versao}
          {fechamento.fechadoEm === null
            ? ''
            : `, gravado em ${formatarData(fechamento.fechadoEm)}`}
          {fechamento.fechadoPor === null ? '' : ` por ${fechamento.fechadoPor}`}.
        </p>
        <p>
          Os valores refletem as regras vigentes no período apurado e foram congelados no momento do
          fechamento.
        </p>
        <p className="mt-2 font-mono text-[10px] tracking-wider text-slate-400 uppercase">
          Documento confidencial
        </p>
      </footer>
    </article>
  );
}

/** Um comprovante na tela, com alternância entre resumido e detalhado. */
export function Comprovante({
  fechamento,
  linha,
  modo,
  onModo,
  onFechar,
}: {
  fechamento: CabecalhoFechamento;
  linha: LinhaResultado;
  modo: ModoComprovante;
  onModo: (m: ModoComprovante) => void;
  onFechar: () => void;
}) {
  return (
    <Moldura onFechar={onFechar} controles={<AlternaModo modo={modo} onModo={onModo} />}>
      <FolhaComprovante fechamento={fechamento} linha={linha} modo={modo} />
    </Moldura>
  );
}

/** Todos os comprovantes de uma vez, um por página. */
export function ComprovantesEmLote({
  fechamento,
  linhas,
  modo,
  onModo,
  onFechar,
}: {
  fechamento: CabecalhoFechamento;
  linhas: LinhaResultado[];
  modo: ModoComprovante;
  onModo: (m: ModoComprovante) => void;
  onFechar: () => void;
}) {
  return (
    <Moldura
      onFechar={onFechar}
      aviso={`${linhas.length} comprovantes, um por página.`}
      controles={<AlternaModo modo={modo} onModo={onModo} />}
    >
      {linhas.map((l, i) => (
        <FolhaComprovante
          key={l.nome}
          fechamento={fechamento}
          linha={l}
          modo={modo}
          primeira={i === 0}
        />
      ))}
    </Moldura>
  );
}

function AlternaModo({
  modo,
  onModo,
}: {
  modo: ModoComprovante;
  onModo: (m: ModoComprovante) => void;
}) {
  return (
    <div className="flex rounded border border-slate-300">
      {(['resumido', 'detalhado'] as const).map((m) => (
        <button
          key={m}
          onClick={() => onModo(m)}
          className={clsx(
            'px-3 py-1.5 text-sm capitalize transition first:rounded-l last:rounded-r',
            modo === m ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50',
          )}
        >
          {m}
        </button>
      ))}
    </div>
  );
}

function Moldura({
  children,
  controles,
  aviso,
  onFechar,
}: {
  children: ReactNode;
  controles?: ReactNode;
  aviso?: string;
  onFechar: () => void;
}) {
  return createPortal(
    <div className="impressao-raiz fixed inset-0 z-50 overflow-y-auto bg-slate-900/40 p-4 print:static print:bg-transparent print:p-0">
      <div className="mx-auto max-w-2xl bg-white p-10 shadow-lg print:max-w-none print:p-0 print:shadow-none">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-3 print:hidden">
          <p className="text-xs text-slate-500">
            {aviso ?? 'Use "Imprimir" e escolha Salvar como PDF.'}
          </p>
          <div className="flex items-center gap-2">
            {controles}
            <Botao tipo="secundario" onClick={() => window.print()}>
              <Printer className="size-4" />
              Imprimir
            </Botao>
            <button
              onClick={onFechar}
              title="Fechar"
              className="rounded p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-900"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

function Titulo({ children }: { children: ReactNode }) {
  return (
    <h2 className="font-mono text-[10px] font-semibold tracking-[0.15em] text-slate-500 uppercase">
      {children}
    </h2>
  );
}

function Dado({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <p className="font-mono text-[9px] tracking-[0.12em] text-slate-500 uppercase">{rotulo}</p>
      <p className="mt-0.5 text-sm font-medium text-slate-900">{valor}</p>
    </div>
  );
}

function Item({
  rotulo,
  detalhe,
  valor,
  forte = false,
}: {
  rotulo: string;
  detalhe?: string;
  valor: string;
  forte?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-dotted border-slate-200 pb-1.5">
      <dt className={forte ? 'font-medium text-slate-900' : 'text-slate-700'}>
        {rotulo}
        {detalhe !== undefined && (
          <span className="block text-[11px] text-slate-500">{detalhe}</span>
        )}
      </dt>
      <dd className={clsx('num whitespace-nowrap', forte && 'font-semibold')}>{valor}</dd>
    </div>
  );
}
